import { createContext, FC, ReactNode, useContext, useState } from 'react'
import { MotionValue, useMotionValue } from 'framer-motion'

import { Notifications } from 'components/notifications'

type PlayerContextType = {
  isHelpDone: boolean
  makeHelpDone: () => void
  isVideoHelpDone: boolean
  makeVideoHelpDone: () => void
  permissionsGranted: boolean
  grantPermissions: () => void
  isMuted: boolean
  toggleMuted: () => void
  masterVolume: number
  changeVolume: (value: number) => void
  radioProgress: MotionValue
  radioWave: number
  setRadioWave: (value: number) => void
  videoProgress: MotionValue
  isPopupVideoShow: boolean
  setPopupVideoShow: (value: boolean) => void
}

const INITIAL_RADIO_PROGRESS = 197

const PlayerContext = createContext<PlayerContextType | null>(null)

export const PlayerContextProvider: FC<{
  children?: ReactNode | ReactNode[]
}> = ({ children }) => {
  const [isHelpDone, setIsHelpDone] = useState(false)
  const makeHelpDone = () => setIsHelpDone(true)

  const [isVideoHelpDone, setIsVideoHelpDone] = useState(false)
  const makeVideoHelpDone = () => setIsVideoHelpDone(true)

  const [permissionsGranted, setPermissionsGranted] = useState(false)
  const grantPermissions = () => setPermissionsGranted(true)

  const [isMuted, setIsMuted] = useState(false)
  const toggleMuted = () => setIsMuted(!isMuted)

  const [masterVolume, setVolume] = useState(1)
  const changeVolume = (value: number) => setVolume(value)

  const radioProgress = useMotionValue(INITIAL_RADIO_PROGRESS)
  const [radioWave, setRadioWave] = useState(0)

  const videoProgress = useMotionValue(0)

  const [isPopupVideoShow, setIsPopupVideoShow] = useState(false)
  const setPopupVideoShow = (value: boolean) => setIsPopupVideoShow(value)

  return (
    <PlayerContext.Provider
      value={{
        isHelpDone,
        makeHelpDone,
        isVideoHelpDone,
        makeVideoHelpDone,
        permissionsGranted,
        grantPermissions,
        isMuted,
        toggleMuted,
        masterVolume,
        changeVolume,
        radioProgress,
        radioWave,
        setRadioWave,
        videoProgress,
        isPopupVideoShow,
        setPopupVideoShow,
      }}
    >
      {children}
      <Notifications />
    </PlayerContext.Provider>
  )
}

export const usePlayerContext = () => {
  const context = useContext(PlayerContext)
  return context as PlayerContextType
}
